/**
 * A tela do Campo: o Movimento do Caixa e o Fluxo do Caixa, nas duas folhas da planilha.
 *
 * **Esta tela é o documento.** O fechamento do Campo em PDF é ela mesma, fotografada por um
 * Chromium dentro do servidor, em duas folhas A4 deitadas. Por isso nada aqui existe só para a
 * tela sem estar marcado com `so-tela`, e por isso a marcação é a do notebook, copiada de
 * `FechamentoDoCampo.tsx`, com as mesmas classes do `papel.css`.
 *
 * **É só leitura, e não é só por enquanto.** No notebook esta tela tem célula que se digita:
 * quem fecha o Campo escolhe as linhas do Fluxo, digita valor por cima do automático e corrige
 * data. Aqui, nada. Assinar o mês continua sendo no notebook, que é quem calcula os totais do
 * retrato. É a decisão 6 da parte 3, "reabrir não refecha".
 *
 * **O mês que o Campo ainda não assinou mostra só o Movimento.** O Fluxo é em parte o trabalho
 * de quem fecha, e esse trabalho mora no notebook até a assinatura. A tela DIZ isso, com essas
 * palavras, em vez de mostrar um bloco vazio que pareceria defeito.
 */
import { useEffect, useRef, useState, type CSSProperties, type JSX } from 'react'
import { AtualizadoAs, useAtualizarSozinho } from '../componentes/AtualizadoAs'
import {
  ehSessaoTerminada, ErroDoServidor, servidor,
  type Eu, type LinhaDoFluxo, type LinhaDoMovimento, type Previa, type TelaDoCampo
} from '../api'
import { BarraDeCima } from '../componentes/BarraDeCima'
import { competenciaPorExtenso, emReais, mesAnterior, mesSeguinte, quandoFoi } from '../dinheiro'
import { ehParaImprimir, enderecoDaTelaDoCampo, enderecoDoCampo, irPara } from '../rotas'

/** O papel escreve 0 como "-", igual ao formato contábil da planilha. */
const dinheiroDoPapel = (v: number): string => (v === 0 ? '-' : emReais(v))

/** `2026-09-05` vira `05/09/2026`. */
function dataDoPapel(data: string): string {
  const [ano, mes, dia] = data.split('-')
  return `${dia}/${mes}/${ano}`
}

const comMaiuscula = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1)

/**
 * O Fluxo comprido aperta as linhas para a folha continuar sendo uma só.
 *
 * **Vinte e quatro é o número do notebook** (`muitasLinhas`, em `FechamentoDoCampo.tsx`), e não
 * um palpite: acima disso a folha A4 deitada não cabe numa página sem a letra menor, e uma
 * folha de Fluxo partida em duas é pior de conferir do que uma folha de letra menor.
 *
 * Foi o retrato do PDF que mostrou isto. Com o número errado, o fechamento de setembro, que
 * tem trinta e uma linhas, saiu em TRÊS folhas em vez de duas, e nenhuma prova de texto pegaria.
 */
const LINHAS_QUE_APERTAM_A_FOLHA = 24

export function Campo({
  eu,
  competenciaDoEndereco,
  versaoDoEndereco,
  aoSair
}: {
  eu: Eu
  competenciaDoEndereco: string | null
  versaoDoEndereco: number | null
  aoSair: () => void
}): JSX.Element {
  const [dados, setDados] = useState<TelaDoCampo | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const paraImprimir = ehParaImprimir()
  /* Só a PRÉVIA se atualiza sozinha: o mês assinado não muda. No papel, nunca. */
  const atualizacao = useAtualizarSozinho(!paraImprimir && (dados === null || dados.previa !== null))
  const { rodada, marcarAtualizado } = atualizacao
  const qualFolha = useRef('')

  useEffect(() => {
    let vale = true
    setErro(null)
    const folha = `${competenciaDoEndereco ?? ''}|${versaoDoEndereco ?? ''}`
    if (qualFolha.current !== folha) setDados(null)
    qualFolha.current = folha

    servidor
      .campo(competenciaDoEndereco, versaoDoEndereco)
      .then((recebido) => {
        if (!vale) return
        setDados(recebido)
        marcarAtualizado()
      })
      .catch((falha: unknown) => {
        if (!vale) return
        if (ehSessaoTerminada(falha)) return aoSair()
        setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível carregar.')
      })

    return () => {
      vale = false
    }
  }, [competenciaDoEndereco, versaoDoEndereco, aoSair, rodada, marcarAtualizado])

  const mes = dados?.competencia ?? competenciaDoEndereco ?? null
  const apertada = (dados?.fluxo?.length ?? 0) > LINHAS_QUE_APERTAM_A_FOLHA

  return (
    <>
      {!paraImprimir && (
        <BarraDeCima eu={eu} aoSair={aoSair} voltarPara={{ endereco: '/', texto: 'Início' }} />
      )}

      {/*
        `data-pronto` é o sinal para o Chromium que fotografa esta tela: ele espera por ele
        antes de imprimir. Sem um sinal explícito a foto sairia da tela "Carregando...", e o
        fechamento do mês viria em branco sem ninguém perceber.
      */}
      <main
        className={`folha folha-deitada folha-campo${apertada ? ' apertada' : ''}`}
        data-pronto={dados !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}
        /*
          `data-previa` acende a marca d'água PRÉVIA no `@media print` do `painel.css`. Ela mora
          na folha, e não no servidor, porque o PDF é esta tela fotografada: uma marca desenhada
          por fora poderia, um dia, sair numa folha e não na outra.
        */
        data-previa={dados?.previa !== null && dados?.previa !== undefined ? 'sim' : 'nao'}
      >
        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {dados === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {dados !== null && (
          <>
            {dados.previa !== null && !paraImprimir && <AtualizadoAs atualizacao={atualizacao} />}
            {mes !== null && !paraImprimir && (
              <nav className="navegar-mes so-tela">
                <button aria-label="Mês anterior" onClick={() => irPara(enderecoDoCampo(mesAnterior(mes)))}>◀</button>
                <strong>{competenciaPorExtenso(mes)}</strong>
                <button aria-label="Mês seguinte" onClick={() => irPara(enderecoDoCampo(mesSeguinte(mes)))}>▶</button>
                {dados.tem_documento && (
                  <>
                    <a className="baixar" href={enderecoDoArquivo('fechamento.pdf', dados)}>
                      Baixar o fechamento em PDF
                    </a>
                    <a className="baixar" href={enderecoDoArquivo('fechamento.xlsx', dados)}>
                      Baixar a planilha em Excel
                    </a>
                  </>
                )}
                {/* A prévia tem PDF próprio, com marca d'água, e nome que começa com PREVIA. */}
                {dados.previa !== null && (
                  <a className="baixar previa" href={enderecoDoArquivo('previa.pdf', dados)}>
                    Baixar a prévia em PDF
                  </a>
                )}
              </nav>
            )}

            {/*
              A fase 6 do espelho: os repasses de todo o Campo e a conferência da maquininha. Fora do
              papel, como os botões das folhas da congregação: esta tela é o documento fotografado.
            */}
            {mes !== null && !paraImprimir &&
              (eu.alcance_das_telas?.repasses_do_campo || eu.alcance_das_telas?.conferencia_do_cartao) && (
              <p className="totais so-tela">
                {eu.alcance_das_telas.repasses_do_campo && (
                  <button className="voltar" onClick={() => irPara(enderecoDaTelaDoCampo('repasses', mes))}>
                    Ver os repasses do Campo ▶
                  </button>
                )}
                {eu.alcance_das_telas.repasses_do_campo && eu.alcance_das_telas.conferencia_do_cartao && ' '}
                {eu.alcance_das_telas.conferencia_do_cartao && (
                  <button className="voltar" onClick={() => irPara(enderecoDaTelaDoCampo('conferencia-do-cartao', mes))}>
                    Ver a conferência da maquininha ▶
                  </button>
                )}
              </p>
            )}

            <Situacao dados={dados} />

            {/* ======================================== FOLHA 1: MOVIMENTO DO CAIXA */}
            <section className="pagina-campo">
              <CabecalhoDaPlanilha dados={dados} titulo={dados.cabecalho.tituloMovimento} />
              <FolhaDoMovimento dados={dados} />
              <div className="assinaturas-campo">
                <div className="pastor">
                  <span />
                  {dados.cabecalho.pastor || ' '}
                  <br />
                  {dados.cabecalho.cargoDoPastor}
                </div>
                <div className="tesouraria">
                  <span />
                  {dados.cabecalho.segundaAssinatura}
                </div>
              </div>
            </section>

            {/* ======================================== FOLHA 2: FLUXO DO CAIXA */}
            {dados.fluxo !== null ? (
              <section className="pagina-campo">
                <CabecalhoDaPlanilha dados={dados} titulo={dados.cabecalho.tituloFluxo} />
                <FolhaDoFluxo fluxo={dados.fluxo} />
              </section>
            ) : (
              <>
                {dados.previa !== null && <ContaDaPrevia previa={dados.previa} dados={dados} />}

                <p className="explicacao so-tela">
                  O <strong>{dados.cabecalho.tituloFluxo}</strong>, com as linhas que o Campo
                  escolheu, aparece depois que o Campo assina o mês no notebook. Ele não é só
                  conta: quem fecha escolhe quais linhas entram, digita o que o sistema não sabe e
                  confere as datas. A conta acima soma as mesmas parcelas dele, e por isso ela é
                  prévia e não documento.
                </p>
              </>
            )}
          </>
        )}
      </main>
    </>
  )
}

/**
 * A CONTA DA PRÉVIA, e o que ainda falta para ela virar o fechamento.
 *
 * Pedido do Cristiano em 12/10/2026. Três coisas governam este bloco:
 *
 * 1. **A marcação é forte de propósito.** "PRÉVIA · mês aberto · não assinado" aparece antes de
 *    qualquer número, em tarja, e a marca d'água PRÉVIA sai no PDF. Um número de dinheiro sem
 *    data e sem assinatura circulando num grupo de WhatsApp é como nasce a conversa "mas no
 *    painel estava escrito outro valor".
 * 2. **Ela diz a idade dos números.** O bloco "o que ainda falta" existe porque uma prévia
 *    bonita e desatualizada é pior do que nenhuma: três congregações sem sincronizar hoje e o
 *    resultado já é de ontem.
 * 3. **Ela não é o Fluxo do Caixa**, e a frase embaixo diz isso. O porquê está em
 *    `src/painel/previa-do-campo.ts`.
 */
function ContaDaPrevia(
  { previa, dados }: { previa: Previa; dados: TelaDoCampo }
): JSX.Element {
  const { conta, falta } = previa

  return (
    <section className="previa-do-campo">
      <p className="tarja-da-previa">
        <strong>PRÉVIA</strong> · mês aberto · não assinado
      </p>

      <h2>A conta de {dados.competencia_por_extenso}, com o que já chegou</h2>

      <table className="grade conta-da-previa">
        <tbody>
          <tr>
            <th>Saldo do mês anterior</th>
            <td className="valor">{emReais(conta.saldo_do_mes_anterior)}</td>
          </tr>
          <tr>
            <th>Entradas das congregações</th>
            <td className="valor">{emReais(conta.entradas_das_congregacoes)}</td>
          </tr>
          {conta.oferta_nao_identificada !== 0 && (
            <tr>
              <th>Oferta não identificada no extrato</th>
              <td className="valor">{emReais(conta.oferta_nao_identificada)}</td>
            </tr>
          )}
          <tr className="subtotal">
            <th>Total das entradas</th>
            <td className="valor">{emReais(conta.total_entradas)}</td>
          </tr>

          <tr>
            <th>Saídas das congregações</th>
            <td className="valor">{emReais(conta.saidas_das_congregacoes)}</td>
          </tr>
          <tr>
            <th>Envio aos departamentos</th>
            <td className="valor">{emReais(conta.envio_aos_departamentos)}</td>
          </tr>
          <tr>
            <th>Repasse à Matriz</th>
            <td className="valor">{emReais(conta.repasse_a_matriz)}</td>
          </tr>
          <tr>
            <th>Recargas de cartão</th>
            <td className="valor">{emReais(conta.recargas_de_cartao)}</td>
          </tr>
          {conta.saidas_pela_conta_do_campo.map((s) => (
            <tr key={s.categoria} className="por-categoria">
              <th>{s.categoria}</th>
              <td className="valor">{emReais(s.valor)}</td>
            </tr>
          ))}
          <tr className="subtotal">
            <th>Total das saídas</th>
            <td className="valor">{emReais(conta.total_saidas)}</td>
          </tr>

          <tr className="resultado">
            <th>Resultado</th>
            <td className="valor">{emReais(conta.resultado)}</td>
          </tr>
        </tbody>
      </table>

      {!conta.mes_anterior_assinado && (
        <p className="aviso-da-previa">
          O mês anterior ainda não foi assinado, então o saldo de partida é zero ou o que alguém
          anotou no notebook. <strong>A conta inteira sobe ou desce junto com ele.</strong>
        </p>
      )}

      {conta.linhas_do_fluxo_ja_ajustadas > 0 && (
        <p className="aviso-da-previa">
          Quem fecha o Campo já começou a trabalhar neste mês:{' '}
          <strong>
            {conta.linhas_do_fluxo_ja_ajustadas === 1
              ? 'uma linha do Fluxo já foi ajustada à mão'
              : `${conta.linhas_do_fluxo_ja_ajustadas} linhas do Fluxo já foram ajustadas à mão`}
          </strong>
          . A partir daí, quem tem o número certo é o notebook.
        </p>
      )}

      <OQueAindaFalta falta={falta} />
    </section>
  )
}

/** O que ainda falta para a prévia virar o fechamento. É ela que dá idade ao número. */
function OQueAindaFalta({ falta }: { falta: Previa['falta'] }): JSX.Element | null {
  const nada =
    falta.sem_sincronizar_hoje.length === 0 &&
    falta.nao_fecharam_o_mes.length === 0 &&
    falta.saidas_esperando.quantas === 0

  if (nada) {
    return (
      <p className="explicacao">
        Todas as congregações sincronizaram hoje, todas fecharam o mês e não há saída esperando
        decisão. A prévia está com tudo o que existe.
      </p>
    )
  }

  return (
    <div className="falta-para-fechar">
      <h3>O que ainda falta</h3>
      <ul>
        {falta.sem_sincronizar_hoje.length > 0 && (
          <li>
            <strong>
              {falta.sem_sincronizar_hoje.length === 1
                ? '1 congregação não sincronizou hoje'
                : `${falta.sem_sincronizar_hoje.length} congregações não sincronizaram hoje`}
            </strong>
            <ul className="miudo">
              {falta.sem_sincronizar_hoje.map((c) => (
                <li key={c.id}>
                  {c.nome}:{' '}
                  {c.ultimo_envio === null
                    ? 'nunca mandou nada'
                    : `último envio ${quandoFoi(c.ultimo_envio)}`}
                </li>
              ))}
            </ul>
          </li>
        )}

        {falta.nao_fecharam_o_mes.length > 0 && (
          <li>
            <strong>
              {falta.nao_fecharam_o_mes.length === 1
                ? '1 congregação ainda não fechou o mês'
                : `${falta.nao_fecharam_o_mes.length} congregações ainda não fecharam o mês`}
            </strong>{' '}
            <span className="explicacao">
              (cada uma entra na conta com a prévia dela, que ainda pode mudar)
            </span>
            <ul className="miudo">
              {falta.nao_fecharam_o_mes.map((c) => (
                <li key={c.id}>
                  {c.nome}
                  {c.travada && ' (travada pelo Campo: os números dela não mudam mais)'}
                </li>
              ))}
            </ul>
          </li>
        )}

        {falta.saidas_esperando.quantas > 0 && (
          <li>
            <strong>
              {falta.saidas_esperando.quantas === 1
                ? '1 saída esperando decisão'
                : `${falta.saidas_esperando.quantas} saídas esperando decisão`}
            </strong>
            , somando {emReais(falta.saidas_esperando.soma)}. Elas ainda não entram na conta.
          </li>
        )}
      </ul>
    </div>
  )
}

/** O endereço do arquivo, com o mês e, quando for uma assinatura antiga, a versão. */
function enderecoDoArquivo(arquivo: string, dados: TelaDoCampo): string {
  const versao = dados.assinatura !== null && !dados.assinatura.e_a_atual
    ? `&versao=${dados.assinatura.versao}`
    : ''
  return `/v1/painel/campo/${arquivo}?competencia=${dados.competencia}${versao}`
}

/** Os seis títulos da planilha, no alto de cada folha. Sem o logo, que não viaja do notebook. */
function CabecalhoDaPlanilha({ dados, titulo }: { dados: TelaDoCampo; titulo: string }): JSX.Element {
  return (
    <div className="cabecalho-campo">
      <div>
        {dados.cabecalho.linhas.map((l, i) => <div key={i}>{l || ' '}</div>)}
        <div>Campo: {dados.cabecalho.campo}</div>
        <div>{titulo}</div>
        <div>Mês: {comMaiuscula(dados.competencia_por_extenso)}</div>
      </div>
    </div>
  )
}

/**
 * Em que situação o mês do Campo está, antes de qualquer número.
 *
 * Quem abre a tela precisa saber, na primeira linha, se o que está vendo é um documento
 * assinado ou o mês andando. Os dois têm a mesma cara de planilha, e confundir um com o outro
 * é mandar para a Matriz um número que ainda ia mudar.
 *
 * **Só na tela**, como no notebook: o papel é a planilha, e nada além dela. Qual versão é
 * aquele papel já está no nome do arquivo que o pastor baixa.
 */
function Situacao({ dados }: { dados: TelaDoCampo }): JSX.Element {
  const a = dados.assinatura

  if (a !== null) {
    return (
      <p className="situacao-do-campo so-tela">
        <span className={`situacao ${a.status === 'FECHADO' ? 'FECHADO' : 'ABERTO'}`}>
          {a.status === 'FECHADO' ? 'fechado e assinado' : 'reaberto'}
        </span>{' '}
        {a.assinado_por !== null && <>Assinado por {a.assinado_por}</>}
        {a.assinado_em !== null && <>, em {dataDoPapel(a.assinado_em.slice(0, 10))}</>}
        {a.versao > 1 && <> · versão {a.versao}</>}
        {!a.e_a_atual && (
          <>
            {' '}
            <strong className="so-tela">
              Esta não é a versão mais recente deste mês: o Campo assinou de novo depois dela.
            </strong>
          </>
        )}
        {a.motivo_da_reabertura !== null && <> · Reaberto: {a.motivo_da_reabertura}</>}
      </p>
    )
  }

  return (
    <p className="situacao-do-campo so-tela">
      <span className="situacao ABERTO">
        {dados.situacao === 'REABERTO' ? 'reaberto' : 'ainda não assinado'}
      </span>{' '}
      {dados.situacao === 'REABERTO'
        ? 'O Campo reabriu este mês, e nenhuma assinatura dele está valendo.'
        : 'O Campo ainda não fechou este mês. O que aparece abaixo é o Movimento como ele está agora.'}
    </p>
  )
}

/**
 * O Movimento do Caixa: uma linha por congregação, e o total.
 *
 * As larguras das colunas são as do `papel.css`, que o notebook mediu na planilha. A
 * congregação que não fechou aparece com as células vazias e a marcação, e não fora da lista:
 * tirar a linha dela faria o Campo parecer ter menos congregações do que tem.
 *
 * **No celular cabe menos de um terço desta folha**, e foi isso que o Cristiano leu como célula
 * vazia em 13/10/2026. A conta: o nome leva 30% e as três colunas de dinheiro 11% cada, então a
 * ENTRADA só começa aos 67% da largura da tabela. Num aparelho de 390px ela está fora da tela, e
 * ao arrastar para buscá-la o nome da congregação sai junto. Por isso cada linha carrega os três
 * números dela ao lado do nome, com `so-tela`: no papel a planilha é a planilha.
 */
function FolhaDoMovimento({ dados }: { dados: TelaDoCampo }): JSX.Element {
  const total = dados.total_do_movimento

  return (
    <>
      {dados.aviso_do_campo_em_dobro !== null && (
        <p className="aviso-da-previa so-tela">{dados.aviso_do_campo_em_dobro}</p>
      )}
      <p className="rola-para-o-lado so-tela">Arraste a folha para o lado para ver todas as colunas.</p>
      <div className="rola-a-folha">
        <table
          className="grade planilha-campo movimento"
          /*
            Quantas colunas de departamento este Campo tem. Quem usa é o `painel.css`, na largura
            mínima da folha: com os 900px fixos de antes, oito departamentos ficavam com 41px de
            coluna cada e os títulos se desenhavam um por cima do outro.
          */
          style={{ '--colunas': dados.colunas.length } as CSSProperties}
        >
          <colgroup>
            <col className="col-congregacao" />
            {dados.colunas.map((c) => <col key={c.id} />)}
            <col className="col-valor" /><col className="col-valor" /><col className="col-valor" />
          </colgroup>
          <thead>
            {/* A linha do rótulo e da conta de cada departamento, em cima da coluna dele. */}
            <tr className="rotulos">
              <th className="sem-borda" />
              {dados.colunas.map((c) => (
                <th key={c.id} className={c.rotulo || c.conta ? undefined : 'sem-borda'}>
                  {c.rotulo && <span>{c.rotulo}</span>}
                  {c.conta && <span>Conta: {c.conta}</span>}
                </th>
              ))}
              <th colSpan={3} className="sem-borda" />
            </tr>
            <tr className="cinza">
              <th>CONGREGAÇÃO</th>
              {dados.colunas.map((c) => <th key={c.id}>{c.titulo}</th>)}
              <th>ENTRADA</th><th>SAÍDA</th><th>SALDO</th>
            </tr>
          </thead>
          <tbody>
            {dados.movimento.map((l) => {
              /*
                A linha tem número quando ela é assinada, travada ou PRÉVIA.

                No mês do Campo já assinado não existe prévia: ali a folha é a fotografia, e a
                linha de quem não tinha fechado continua em branco, como o notebook a assinou.
              */
              const temNumero = l.fechou || l.travada || l.e_previa
              return (
                <tr key={l.congregacao_id} className={temNumero ? undefined : 'nao-fechou'}>
                  <td>
                    <div className="nome-da-congregacao"><span>{l.nome}</span></div>
                    {/* A marcação é só da tela: o papel leva o nome e o número. */}
                    {!l.fechou && (
                      <div className="situacao-da-congregacao so-tela">
                        <span className={l.e_previa ? 'marca-previa' : 'marca-nao-fechou'}>
                          {marcaDaLinha(l)}
                        </span>
                      </div>
                    )}
                    {/*
                      OS TRÊS NÚMEROS DA LINHA, AO LADO DO NOME, SÓ NA TELA ESTREITA.

                      ENTRADA, SAÍDA e SALDO são as três ÚLTIMAS colunas da planilha, e a tabela
                      é mais larga que o telefone: eles são justamente os números que exigem
                      arrastar a folha inteira, e nessa altura o nome já saiu da tela. O
                      `painel.css` esconde este bloco onde a folha cabe inteira.

                      No papel ele não entra. A planilha do Campo é um documento, e o que ela tem
                      é o que o notebook imprime.
                    */}
                    {temNumero && (
                      <div className="numeros-da-linha so-tela">
                        <span>entrou <strong>{emReais(l.entrada)}</strong></span>
                        <span>saiu <strong>{emReais(l.saida)}</strong></span>
                        <span>saldo <strong>{emReais(l.saldo)}</strong></span>
                      </div>
                    )}
                  </td>
                  {l.por_departamento.map((v, j) => (
                    <td key={j} className="valor">
                      {temNumero && v !== 0 ? emReais(v) : ''}
                    </td>
                  ))}
                  <td className="valor">{temNumero ? dinheiroDoPapel(l.entrada) : ''}</td>
                  <td className="valor">{temNumero ? dinheiroDoPapel(l.saida) : ''}</td>
                  <td className="valor">{dinheiroDoPapel(l.saldo)}</td>
                </tr>
              )
            })}
            <tr className="cinza total">
              <td>TOTAL</td>
              {total.por_departamento.map((v, j) => (
                <td key={j} className="valor">{dinheiroDoPapel(v)}</td>
              ))}
              <td className="valor">{dinheiroDoPapel(total.entrada)}</td>
              <td className="valor">{dinheiroDoPapel(total.saida)}</td>
              <td className="valor">{dinheiroDoPapel(total.saldo)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/*
        Os totais escritos embaixo da folha, SÓ NA TELA.

        No celular a tabela rola para o lado, e ENTRADA, SAÍDA e SALDO são as últimas colunas:
        os três números mais importantes seriam os únicos que exigem arrastar para ver.

        No papel, não: a planilha do Campo é um documento, e o que ela tem é o que o notebook
        imprime. Uma frase a mais aqui é uma frase que o papel assinado não tem. Foi o retrato
        do PDF que mostrou isso, com a frase empurrando uma terceira folha em branco.
      */}
      <p className="explicacao so-tela">
        Entrou <strong>{emReais(total.entrada)}</strong>, saiu dos caixas{' '}
        <strong>{emReais(total.saida)}</strong>, saldo <strong>{emReais(total.saldo)}</strong>.
        {dados.nao_fecharam > 0 && (
          <> {fraseDeQuemNaoFechou(dados.nao_fecharam, dados.tem_documento)}.</>
        )}
      </p>
    </>
  )
}

/** O que a linha de quem não assinou o mês diz embaixo do nome da congregação. */
function marcaDaLinha(l: LinhaDoMovimento): string {
  if (!l.e_previa) return '(não fechou)'
  return l.travada ? '(travada, prévia)' : '(prévia)'
}

/**
 * A frase sobre quem não fechou, embaixo da folha.
 *
 * **No mês assinado ela é outra**, e tem de ser: ali a folha é a fotografia, e quem não tinha
 * fechado está zerada nela para sempre. É só no mês aberto que a linha dela é a prévia.
 */
function fraseDeQuemNaoFechou(quantas: number, assinado: boolean): string {
  const quem = quantas === 1 ? '1 congregação' : `${quantas} congregações`
  const naoFechou = quantas === 1 ? 'não fechou o mês' : 'não fecharam o mês'

  if (assinado) {
    return `${quem} ${naoFechou} e ${quantas === 1 ? 'aparece zerada' : 'aparecem zeradas'}`
  }
  return `${quem} ainda ${naoFechou} e ${quantas === 1 ? 'entra com a prévia dela' : 'entram com a prévia delas'}`
}

/**
 * O Fluxo do Caixa: o extrato do mês da conta do Campo, com o saldo corrente linha a linha.
 *
 * A linha sem valor fica, com a data em "-": ela é a linha da planilha de papel, impressa e
 * esperando o lançamento. Tirar as vazias mudaria a folha de um mês para o outro.
 */
function FolhaDoFluxo({ fluxo }: { fluxo: LinhaDoFluxo[] }): JSX.Element {
  const ultima = fluxo.length - 1

  return (
    <>
      <p className="rola-para-o-lado so-tela">Arraste a folha para o lado para ver todas as colunas.</p>
      <div className="rola-a-folha">
        <table className="grade planilha-campo fluxo">
          <colgroup>
            <col className="col-data" /><col className="col-descricao" />
            <col style={{ width: '14%' }} /><col style={{ width: '14%' }} /><col style={{ width: '16%' }} />
          </colgroup>
          <thead>
            <tr className="cinza">
              <th>DATA</th><th>DESCRIÇÃO</th><th>ENTRADA</th><th>SAIDA</th><th>SALDO</th>
            </tr>
          </thead>
          <tbody>
            {fluxo.map((l, i) => (
              <tr key={l.chave} className={l.nova ? 'linha-nova' : undefined}>
                <td className="centro data-do-fluxo">{l.data ? dataDoPapel(l.data) : '-'}</td>
                <td>{l.descricao}</td>
                <td className="valor">{dinheiroDoPapel(l.entrada)}</td>
                <td className="valor">{dinheiroDoPapel(l.saida)}</td>
                {/* Negrito nas pontas: o saldo de partida e o de chegada são os que se conferem. */}
                <td className="valor">
                  {i === 0 || i === ultima ? <strong>{dinheiroDoPapel(l.saldo)}</strong> : dinheiroDoPapel(l.saldo)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Só na tela, pelo mesmo motivo da folha do Movimento. */}
      <p className="explicacao so-tela">
        Saldo no fim do mês: <strong>{emReais(fluxo[ultima]?.saldo ?? 0)}</strong>. É o número que
        se confere com o extrato da conta do Campo.
      </p>
    </>
  )
}
